/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Box, Grid, TextField, IconButton } from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CustomTextField } from '../../index';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ContainedButton } from '../../index';
import { useState } from 'react';

export const EmployeeTrainingsTab = (props) => {
  const handleChange = (index, field, value) => {
    props.setEmpTrn((prev) => {
      const updated = [...prev];

      if (field === 'hours') {
        const parsed = value === '' ? undefined : parseFloat(value);
        updated[index] = { ...updated[index], [field]: parsed };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }

      return updated;
    });
  };

  const handleAdd = () => {
    props.setEmpTrn((prev) => [
      {
        empid: props.empid,
        trainingtitle: '',
        startdate: undefined,
        enddate: undefined,
        hours: undefined,
        sponsor: '',
      },
      ...prev,
    ]);
  };

  const handleDelete = (index) => {
    props.setEmpTrn((prev) => prev.filter((_, i) => i !== index));
  };

  const boxStyle = {
    mx: 0,
    my: 1,
    px: 3,
    py: 2,
  };
  const [startTouched, setStartTouched] = useState(false);

  return (
    <Box sx={{ ...boxStyle, maxHeight: '400px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {!props.viewOnly && (
          <ContainedButton
            startIcon={<Add />}
            onClick={handleAdd}
            variant="contained"
            btnTitle={'Add Training'}
            style={{ width: 'auto' }}
          />
        )}
      </Box>
      {props.emptrn.map((training, index) => (
        <Box
          key={index}
          sx={{
            bgcolor: 'rgb(26, 32, 44, 0.6)',
            display: 'flex',
            justifyContent: 'start',
            p: 2,
            mt: 2,
            borderRadius: '5px',
            position: 'relative',
          }}
        >
          {!props.viewOnly && (
            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(index)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                mr: 1,
              }}
            >
              <Close />
            </IconButton>
          )}

          <Grid container columnSpacing={2} sx={{ mt: 3 }}>
            <Grid item xs={6}>
              <CustomTextField
                error={!training.trainingtitle}
                errorMsg="Please enter a training title"
                label="Training Title"
                value={training.trainingtitle || ''}
                onChange={(e) =>
                  handleChange(index, 'trainingtitle', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!training.hours}
                type="number"
                errorMsg="Please enter the number of hours"
                label="Number of Hours"
                value={training.hours || ''}
                onChange={(e) => handleChange(index, 'hours', e.target.value)}
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={
                    training.startdate ? new Date(training.startdate) : null
                  }
                  maxDate={
                    training.enddate ? new Date(training.enddate) : undefined
                  }
                  onChange={(newValue) =>
                    handleChange(index, 'startdate', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  renderInput={(params) => {
                    const showError = startTouched && !training.startdate;
                    return (
                      <TextField
                        onFocus={() => setStartTouched(true)}
                        margin="normal"
                        required
                        fullWidth
                        {...params}
                        error={showError}
                        helperText={showError ? 'Start Date is required' : ''}
                        inputProps={{
                          ...params.inputProps,
                          style: {
                            cursor: props.viewOnly ? 'default' : undefined,
                          },
                        }}
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={training.enddate ? new Date(training.enddate) : null}
                  minDate={
                    training.startdate
                      ? new Date(training.startdate)
                      : undefined
                  }
                  onChange={(newValue) =>
                    handleChange(index, 'enddate', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  renderInput={(params) => (
                    <TextField
                      margin="normal"
                      fullWidth
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          cursor: props.viewOnly ? 'default' : undefined,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a sponsor"
                label="Sponsor"
                value={training.sponsor || ''}
                onChange={(e) => handleChange(index, 'sponsor', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
